import React, { useEffect } from 'react';
import FormGroup from '@material-ui/core/FormGroup';
import { FieldArray, FieldArrayRenderProps } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { useQuery } from '@apollo/client';
import styled from 'styled-components';
import { DeliveredFieldDefinitionModel, PhoneNumberModel } from '@utmost.co/enterprise-models';
import { PrimaryButton, SystemIcon } from '@utmost.co/utmost-design-system';
import { SmartformElementData } from '../../types';
import { getElementFields } from '../../forms/utils/getElementFields';
import { PermissionHelper, SelectValue } from '../../atoms/index';
import { useFieldSmartFormContext } from '../../forms/utils/useSmartFormContext';
import { Label } from '../../atoms/Label/Label';
import { HelpText } from '../../atoms/HelpText/HelpText';
import { DEFAULT_CALLING_CODE } from '../../forms/utils';
import { logger } from '../../logger';
import { GET_DEVICE_TYPES } from './GET_DEVICE_TYPES';
import { PhoneNumberRow } from './PhoneNumberRow';

const createEmptyPhoneNumberModel = (primary = false): Partial<Omit<PhoneNumberModel, 'entityType'>> => ({
  areaCode: '',
  countryCode: DEFAULT_CALLING_CODE,
  deliveredFieldDefinitions: {},
  deviceType: '',
  e164Number: '',
  extension: '',
  number: '',
  primary,
  types: [],
  uid: uuidv4()
});

const emptyValue: any[] = [];

export const PhoneNumberMultiple: React.FC<SmartformElementData> = (props) => {
  const { data, error: apolloError } = useQuery<{
    deliveredFieldDefinition: DeliveredFieldDefinitionModel;
  }>(GET_DEVICE_TYPES);
  if (apolloError) {
    logger.error(apolloError);
  }
  const [phoneNumberMultiMeta, addButtonMeta] = getElementFields<2>(props).required;
  const componentName = phoneNumberMultiMeta.name;
  const {
    setFieldValues,
    readonly,
    setFieldTouched,
    getFieldPermissions,
    permissionDebugger,
    field: { value, disabled, error }
  } = useFieldSmartFormContext<PhoneNumberModel[]>(componentName);

  const phonePermissions = getFieldPermissions(componentName);
  const isDisabled = disabled || readonly;
  const allPhones = value ?? emptyValue;
  const deviceTypes: SelectValue[] =
    data?.deliveredFieldDefinition?.values?.map(({ label, value }) => ({
      id: value,
      key: value,
      label,
      value
    })) ?? emptyValue;

  useEffect(() => {
    if (allPhones?.length && !readonly) {
      allPhones.forEach((phone, index) => {
        setFieldTouched(`${componentName}[${index}]`, true, true);
      });
    }
  }, [allPhones, componentName, readonly, setFieldTouched]);

  const updatePrimaryRadios = (rowIndexClicked: number) => {
    const updatePrimarys = allPhones.reduce(
      (primaryAccumulator: Record<string, any>, phoneNumberModel: PhoneNumberModel, index: number) => ({
        ...primaryAccumulator,
        [`${componentName}[${index}].primary`]: index === rowIndexClicked
      }),
      {}
    );
    setFieldValues({ ...updatePrimarys });
  };

  const componentLevelError = isComponentLevelError(error) ? error : undefined;

  const removeNumber = (arrayHelpers: FieldArrayRenderProps, numberIndex: number) => {
    if (allPhones[numberIndex]?.primary && allPhones.length > 1) {
      updatePrimaryRadios(numberIndex === 0 ? 1 : 0);
    }
    arrayHelpers.remove(numberIndex);
  };

  return (
    <FormGroup id={componentName} data-test={`${componentName}-field-control`}>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <Label
        text={phoneNumberMultiMeta.label}
        dataTest={componentName}
        error={!!error}
        required={phoneNumberMultiMeta.validation.required}
      />
      <HelpText
        text={phoneNumberMultiMeta.helpText}
        error={componentLevelError}
        dataTest={componentName}
        hideWhenEmpty
      />
      <FieldArray name={componentName}>
        {(arrayHelpers: FieldArrayRenderProps) => (
          <>
            {allPhones.map((phone: PhoneNumberModel, index: number) => {
              const uniqueKey = phone?.uid ?? uuidv4();
              const rowName = `${componentName}[${index}]`;
              return (
                <PhoneNumberRow
                  name={rowName}
                  disabled={isDisabled}
                  key={uniqueKey}
                  readonly={readonly}
                  rowIndex={index}
                  deviceTypes={deviceTypes}
                  onClickPrimaryRadio={updatePrimaryRadios}
                  onClickDelete={(rowIndexToRemove: number) => {
                    removeNumber(arrayHelpers, rowIndexToRemove);
                  }}
                />
              );
            })}
            {!isDisabled && !readonly && (
              <StyledButtonContainer>
                <PrimaryButton.Text
                  onClick={() => {
                    arrayHelpers.push(createEmptyPhoneNumberModel(!allPhones.length));
                  }}
                >
                  <SystemIcon name="add" />
                  <span data-test="button-label">{addButtonMeta?.label || 'Add a phone number'}</span>
                </PrimaryButton.Text>
              </StyledButtonContainer>
            )}
          </>
        )}
      </FieldArray>
      {permissionDebugger && <PermissionHelper permission={phonePermissions} />}
    </FormGroup>
  );
};

const StyledButtonContainer = styled.div`
  margin: 8px 0;

  hr + & {
    margin: -8px 0 8px 0;
  }
`;

function isComponentLevelError(error?: string[] | string): error is string {
  return typeof error === 'string';
}
